import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlay, FaArrowRight, FaClock } from 'react-icons/fa';
import { motion } from 'framer-motion';

interface TutorialCardProps {
  id: string;
  title: string;
  description?: string;
  video_url: string;
  thumb_url: string;
  duration_s: number;
  chapters?: Array<{ t: number; label: string }>;
  cta: { label: string; route: string };
}

export default function TutorialCard({
  title,
  description,
  video_url,
  thumb_url,
  duration_s,
  chapters,
  cta
}: TutorialCardProps) {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Prevent video downloading and unauthorized access
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Disable right-click context menu on video
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    // Prevent keyboard shortcuts when video or container is focused
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only block if video is focused or event target is within video container
      const target = e.target as HTMLElement;
      if (target === video || video.contains(target) || target.closest('.video-container')) {
        // Block Ctrl+S, Ctrl+Shift+S (save shortcuts)
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          return false;
        }
      }
    };

    // Prevent drag and drop
    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    // Disable download controls and picture-in-picture
    video.setAttribute('controlsList', 'nodownload noplaybackrate');
    video.setAttribute('disablePictureInPicture', 'true');

    // Add event listeners
    video.addEventListener('contextmenu', handleContextMenu);
    video.addEventListener('dragstart', handleDragStart);
    video.addEventListener('keydown', handleKeyDown);

    return () => {
      video.removeEventListener('contextmenu', handleContextMenu);
      video.removeEventListener('dragstart', handleDragStart);
      video.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPlaying]);

  const handlePlay = () => {
    setIsPlaying(true);
    videoRef.current?.play();
  };

  const handleChapterJump = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      if (!isPlaying) {
        handlePlay();
      }
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200 shadow-md my-3"
    >
      <div className="flex items-start gap-2 mb-3">
        <FaPlay className="text-blue-600 mt-1 flex-shrink-0" />
        <div>
          <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
          {description && (
            <p className="text-xs text-gray-600 mt-1">{description}</p>
          )}
        </div>
      </div>

      <div 
        className="video-container relative rounded-lg overflow-hidden bg-black mb-3 select-none"
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {!isPlaying && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <button
              onClick={handlePlay}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-transform hover:scale-110 z-20"
            >
              <FaPlay size={20} />
            </button>
            <img
              src={thumb_url}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover opacity-90 pointer-events-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onError={(e) => {
                // Fallback to a placeholder if image fails to load
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        <video
          ref={videoRef}
          src={video_url}
          controls={isPlaying}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          className="w-full aspect-video pointer-events-auto"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
          onPlay={() => setIsPlaying(true)}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          onError={(e) => {
            console.error('Video load error:', e);
            // Video failed to load - could be placeholder URL
          }}
        />
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-600 mb-3">
        <FaClock />
        <span>{formatDuration(duration_s)}</span>
      </div>

      {chapters && chapters.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-700 mb-2">Chapters:</p>
          <div className="flex flex-wrap gap-2">
            {chapters.map((chapter, idx) => (
              <button
                key={idx}
                onClick={() => handleChapterJump(chapter.t)}
                className="text-xs bg-white hover:bg-blue-100 text-blue-700 px-2 py-1 rounded border border-blue-200 transition-colors"
              >
                {formatDuration(chapter.t)} - {chapter.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => navigate(cta.route)}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
      >
        {cta.label}
        <FaArrowRight size={14} />
      </button>
    </motion.div>
  );
}

